/**
 * Reusable reference helpers shared across chart types: horizontal/vertical
 * reference lines (limits, mean/median/percentiles) and the 1:1 identity line
 * (previously inlined in scatter). Keeping these here means colors come from
 * the semantic palette and the logic is testable in isolation.
 */

import type { ChartTrace } from '../types';
import { SEMANTIC } from '../theme/palette';

export type RefAxis = 'x' | 'y';

/**
 * A Plotly shape for a reference line. `axis:'y'` is horizontal (spans the
 * plot width at a Y value); `axis:'x'` is vertical (spans the height at an X
 * value). The orthogonal extent uses the axis DOMAIN (`x domain`/`y domain`),
 * not paper, so the line spans the plot area only — even when the plot is
 * shrunk (e.g. `xaxis.domain:[0,0.6]` to reserve a right-side stats box) it
 * stays inside the plot instead of bleeding into the gutter. Equivalent to
 * paper when the domain is the full [0,1].
 */
export function referenceLineShape(opts: {
  value: number;
  axis: RefAxis;
  color?: string;
  dash?: string;
  width?: number;
}): ChartTrace {
  const { value, axis, color = SEMANTIC.reference, dash = 'dash', width = 2 } = opts;
  const line = { color, width, dash };
  return axis === 'y'
    ? { type: 'line', xref: 'x domain', x0: 0, x1: 1, y0: value, y1: value, line }
    : { type: 'line', yref: 'y domain', y0: 0, y1: 1, x0: value, x1: value, line };
}

/**
 * The 1:1 identity line from (min,min) to (max,max). Extracted from scatter.
 *
 * `scale` follows the figure (see ../theme/scale): a 1px hairline that reads
 * correctly on a 650px card becomes invisible once the same figure is drawn at
 * 1000px and then shrunk into a PDF column.
 */
export function identityLineTrace(
  min: number,
  max: number,
  color = SEMANTIC.identity,
  scale = 1
): ChartTrace {
  return {
    x: [min, max],
    y: [min, max],
    mode: 'lines',
    type: 'scatter',
    name: '1:1',
    line: { dash: 'dash', width: IDENTITY_LINE_WIDTH * scale, color },
  };
}

/** Stroke width (px, at the reference figure width) of the 1:1 identity line. */
const IDENTITY_LINE_WIDTH = 1;
